import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { isApiError, toUserMessage } from '@/lib/http/api-error';

import { asistenteApi, asistenteKeys } from '../asistente.api';
import {
  selectConversacionId,
  selectHistory,
  selectLoading,
  selectMessages,
  useAsistenteStore,
} from '../asistente.store';

interface UseAsistenteChatOptions {
  empresa?: string | null;
  sucursal?: string | null;
}

/**
 * Conversación con el asistente.
 *
 * Se usa un `useCallback` en lugar de `useMutation` porque el resultado se
 * acumula en el hilo de mensajes: los errores también se agregan como un mensaje
 * para que el usuario no pierda el contexto de lo que preguntó.
 */
export function useAsistenteChat({ empresa, sucursal }: UseAsistenteChatOptions = {}) {
  const messages = useAsistenteStore(selectMessages);
  const loading = useAsistenteStore(selectLoading);
  const conversacionId = useAsistenteStore(selectConversacionId);
  const addMessage = useAsistenteStore((state) => state.addMessage);
  const setLoading = useAsistenteStore((state) => state.setLoading);
  const setConversacion = useAsistenteStore((state) => state.setConversacion);
  const reset = useAsistenteStore((state) => state.reset);
  const queryClient = useQueryClient();

  const send = useCallback(
    async (text: string) => {
      const message = text.trim();
      if (!message) return;

      const history = selectHistory(useAsistenteStore.getState().messages);
      const conversacion = useAsistenteStore.getState().conversacionId;

      addMessage({ role: 'user', content: message });
      setLoading(true);

      try {
        const response = await asistenteApi.chat({
          message,
          history,
          conversacionId: conversacion,
          empresa,
          sucursal,
        });

        setConversacion(response.conversacion_id ?? conversacion, response.conversacion_titulo ?? null);

        addMessage({
          role: 'assistant',
          content: response.reply,
          charts: response.charts ?? [],
          cards: response.cards ?? [],
          tablas: response.tablas ?? [],
          enlaces: response.enlaces ?? [],
          acciones: response.acciones ?? [],
          consultaId: response.consulta_id ?? null,
          tools: response.tools_used ?? [],
        });

        // El hilo acaba de crecer: el historial (título y fecha) cambia.
        void queryClient.invalidateQueries({ queryKey: asistenteKeys.conversaciones('') });
      } catch (error) {
        addMessage({ role: 'error', content: resolveErrorMessage(error) });
      } finally {
        setLoading(false);
      }
    },
    [addMessage, empresa, queryClient, setConversacion, setLoading, sucursal],
  );

  return { messages, loading, send, reset, conversacionId };
}

/** Mensaje de error orientado al usuario (incluye la causa del proveedor de IA). */
function resolveErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.isNetworkError) {
      return 'No se pudo contactar al servidor. Verifica la conexión e inténtalo de nuevo.';
    }

    if (error.status === 503) {
      return `${error.message} Contacta al administrador para habilitar el asistente.`;
    }

    return error.message;
  }

  return toUserMessage(error);
}
